import React, { useState, useRef, useEffect, useCallback } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { createTender, createTenderDocument, updateTender, createGapItems, createTenderTasks } from '../../services/tenderService';
import { getChatCompletion } from '../../services/aiIntegrations/chatCompletion';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const CATEGORIES = ['IT', 'Construction', 'Consulting', 'Supply', 'Maintenance', 'Telecom', 'Oil & Gas', 'Healthcare', 'Other'];
const DEPARTMENTS = ['Engineering', 'Finance', 'HR', 'Legal', 'Procurement', 'Operations', 'IT', 'Sales'];

const ACCEPTED_TYPES = {
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.ms-excel': 'XLS',
  'application/zip': 'ZIP',
  'application/x-zip-compressed': 'ZIP',
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });
}

function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
}

const ANALYSIS_STEPS = [
  'Parsing document content...',
  'Extracting basic information...',
  'Analyzing scope of work...',
  'Identifying technical requirements...',
  'Analyzing commercial terms...',
  'Checking legal requirements...',
  'Evaluating HSE requirements...',
  'Running compliance engine...',
  'Generating gap analysis...',
  'Creating AI recommendations...',
  'Generating executive summary...',
  'Finalizing analysis report...',
];

const REQUIRED_DOCS_CHECKLIST = [
  'Company Profile',
  'Commercial Registration',
  'VAT Certificate',
  'ISO Certificates',
  'Financial Statements',
  'Experience Certificates',
  'Similar Projects',
  'Engineer CVs',
  'Equipment List',
  'Organization Chart',
  'Method Statement',
  'HSE Plan',
  'Bank Guarantee',
  'Tender Forms',
  'BOQ',
  'Technical Proposal',
  'Commercial Proposal',
];

const AiTenderAnalyzer = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [form, setForm] = useState({
    tenderName: '',
    tenderNumber: '',
    clientName: '',
    projectName: '',
    closingDate: '',
    submissionDate: '',
    estimatedValue: '',
    tenderCategory: 'IT',
    department: 'Engineering',
    description: '',
  });
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [savedTenderId, setSavedTenderId] = useState(null);
  const [extractingDetails, setExtractingDetails] = useState(false);
  const stepTimerRef = useRef(null);

  const handleFormChange = (e) => {
    const { name, value } = e?.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileDrop = useCallback((e) => {
    e?.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e?.dataTransfer?.files || []);
    addFiles(dropped);
  }, []);

  const addFiles = (newFiles) => {
    const valid = newFiles?.filter((f) => ACCEPTED_TYPES?.[f?.type]);
    if (valid?.length < newFiles?.length) {
      toast?.error('Some files were skipped. Accepted: PDF, DOCX, XLSX, ZIP');
    }
    setFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name));
      const toAdd = valid?.filter((f) => !existing?.has(f?.name));
      if (toAdd?.length > 0) {
        autoExtractTenderDetails(toAdd);
      }
      return [...prev, ...toAdd];
    });
  };

  const autoExtractTenderDetails = async (newFiles) => {
    const primaryFile = newFiles?.[0];
    if (!primaryFile) return;

    setExtractingDetails(true);
    try {
      const b64 = await fileToBase64(primaryFile);
      const fileType = ACCEPTED_TYPES?.[primaryFile?.type] || primaryFile?.type;

      const systemPrompt = `You are a tender document parser. Extract key tender details from the document and return ONLY valid JSON with no markdown or explanation.`;

      const extractionPrompt = `Extract the following fields from this tender document. Return ONLY valid JSON:
{
  "tenderName": "tender/project title or name",
  "tenderNumber": "tender reference or number",
  "clientName": "issuing organization or client name",
  "projectName": "project name if different from tender name",
  "closingDate": "closing/deadline date in YYYY-MM-DD format or null",
  "submissionDate": "submission date in YYYY-MM-DD format or null",
  "estimatedValue": "numeric value only (no currency symbols) or null",
  "tenderCategory": "one of: IT, Construction, Consulting, Supply, Maintenance, Telecom, Oil & Gas, Healthcare, Other",
  "department": "one of: Engineering, Finance, HR, Legal, Procurement, Operations, IT, Sales",
  "description": "brief 1-2 sentence description of the tender scope"
}
If a field cannot be determined, use null or empty string. For dates, use YYYY-MM-DD format only.`;

      const userContent = fileType === 'PDF'
        ? [
            { type: 'text', text: extractionPrompt },
            { type: 'file', file: { file_data: b64, filename: primaryFile?.name } },
          ]
        : [
            { type: 'text', text: `${extractionPrompt}\n\nFile: ${primaryFile?.name} [${fileType}] — Extract details based on the filename and any available context.` },
          ];

      const response = await getChatCompletion(
        'OPEN_AI',
        'gpt-4.1',
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userContent },
        ],
        { max_completion_tokens: 800 }
      );

      const raw = response?.choices?.[0]?.message?.content || '{}';
      const cleaned = raw?.replace(/```json\n?/g, '')?.replace(/```\n?/g, '')?.trim();
      const extracted = JSON.parse(cleaned);

      setForm((prev) => ({
        tenderName: extracted?.tenderName || prev?.tenderName,
        tenderNumber: extracted?.tenderNumber || prev?.tenderNumber,
        clientName: extracted?.clientName || prev?.clientName,
        projectName: extracted?.projectName || prev?.projectName,
        closingDate: extracted?.closingDate || prev?.closingDate,
        submissionDate: extracted?.submissionDate || prev?.submissionDate,
        estimatedValue: extracted?.estimatedValue ? String(extracted?.estimatedValue) : prev?.estimatedValue,
        tenderCategory: CATEGORIES?.includes(extracted?.tenderCategory) ? extracted?.tenderCategory : prev?.tenderCategory,
        department: DEPARTMENTS?.includes(extracted?.department) ? extracted?.department : prev?.department,
        description: extracted?.description || prev?.description,
      }));

      toast?.success('Tender details auto-extracted from document!');
    } catch (err) {
      console.error('Auto-extraction error:', err);
      // Silent fail — user can fill manually
    } finally {
      setExtractingDetails(false);
    }
  };

  const removeFile = (name) => setFiles((prev) => prev?.filter((f) => f?.name !== name));

  const buildAIPrompt = () => `You are an expert tender analyst for IT and engineering companies in Oman. Analyze the provided tender documents and extract ALL information into a comprehensive structured JSON.

Tender context:
- Name: ${form?.tenderName}
- Number: ${form?.tenderNumber || 'Not specified'}
- Client: ${form?.clientName || 'Not specified'}
- Project: ${form?.projectName || 'Not specified'}
- Category: ${form?.tenderCategory}
- Department: ${form?.department}
- Description: ${form?.description || 'Not provided'}

Return ONLY valid JSON with this exact structure (no markdown, no explanation):
{
  "basicInfo": {
    "tenderName": "extracted or provided name",
    "tenderNumber": "extracted number",
    "clientName": "extracted client",
    "projectName": "extracted project name",
    "tenderType": "e.g. Open/Restricted/Direct",
    "tenderCategory": "category",
    "country": "Oman",
    "city": "extracted city",
    "issueDate": "extracted date or null",
    "closingDate": "extracted date or null",
    "submissionDate": "extracted date or null",
    "tenderValue": "extracted value or null",
    "contractDuration": "e.g. 12 months",
    "bidValidity": "e.g. 90 days"
  },
  "projectInfo": {
    "scopeOfWork": ["item1", "item2"],
    "projectDescription": "detailed description",
    "deliverables": ["deliverable1", "deliverable2"],
    "technicalScope": ["scope item1"],
    "commercialScope": ["scope item1"],
    "projectObjectives": ["objective1"],
    "expectedTimeline": "description of timeline"
  },
  "technicalRequirements": {
    "requiredEngineers": [{"role": "Senior Engineer", "count": 2, "experience": "5 years", "specialization": "Fiber Optics"}],
    "requiredCertifications": ["ISO 9001", "CISA"],
    "requiredEquipment": ["OTDR Tester", "Cable Blowing Machine"],
    "requiredVehicles": ["Pickup Truck", "Crane"],
    "requiredSoftware": ["AutoCAD", "MS Project"],
    "requiredExperience": ["5 years in similar projects"],
    "requiredStandards": ["IEC 60793", "TIA-568"],
    "requiredMethodology": ["Agile", "PRINCE2"],
    "requiredMaterials": ["Fiber optic cable Cat6A"]
  },
  "commercialRequirements": {
    "bidBond": "e.g. 2% of tender value",
    "performanceBond": "e.g. 10% of contract value",
    "bankGuarantee": "details",
    "paymentTerms": "e.g. 30 days net",
    "commercialProposal": "requirements",
    "financialRequirements": ["Audited statements 2023-2024"],
    "taxRequirements": ["VAT registration required"],
    "pricingInstructions": ["Fixed price contract", "No price escalation"]
  },
  "legalRequirements": {
    "companyRegistration": "requirements",
    "chamberCertificate": "required/not required",
    "vatCertificate": "required/not required",
    "licenses": ["Telecom license", "Civil works license"],
    "isoCertificates": ["ISO 9001:2015", "ISO 27001"],
    "nda": "required/not required",
    "contractConditions": ["FIDIC contract", "Liquidated damages 0.5%/week"],
    "complianceRequirements": ["Omanization 35%", "Local content preference"]
  },
  "hseRequirements": {
    "hsePlan": "required/not required",
    "safetyCertificates": ["NEBOSH", "IOSH"],
    "ppeRequirements": ["Hard hat", "Safety vest", "Safety boots"],
    "riskAssessment": "required/not required",
    "environmentalRequirements": ["Environmental impact assessment", "Waste management plan"]
  },
  "requiredDocuments": [
    {"name": "Company Profile", "required": true, "notes": ""},
    {"name": "Commercial Registration", "required": true, "notes": ""},
    {"name": "VAT Certificate", "required": true, "notes": ""},
    {"name": "ISO Certificates", "required": true, "notes": "ISO 9001 mandatory"},
    {"name": "Financial Statements", "required": true, "notes": "Last 2 years audited"},
    {"name": "Experience Certificates", "required": true, "notes": ""},
    {"name": "Similar Projects", "required": true, "notes": "Minimum 3 projects"},
    {"name": "Engineer CVs", "required": true, "notes": ""},
    {"name": "Equipment List", "required": true, "notes": ""},
    {"name": "Organization Chart", "required": false, "notes": ""},
    {"name": "Method Statement", "required": true, "notes": ""},
    {"name": "HSE Plan", "required": true, "notes": ""},
    {"name": "Bank Guarantee", "required": true, "notes": ""},
    {"name": "Tender Forms", "required": true, "notes": "Signed and stamped"},
    {"name": "BOQ", "required": true, "notes": "Priced BOQ"},
    {"name": "Technical Proposal", "required": true, "notes": ""},
    {"name": "Commercial Proposal", "required": true, "notes": "Sealed envelope"}
  ],
  "aiAnalysis": {
    "executiveSummary": "comprehensive one-paragraph summary",
    "scopeSummary": "brief scope summary",
    "keyRisks": ["risk1", "risk2", "risk3"],
    "majorRequirements": ["req1", "req2"],
    "criticalConditions": ["condition1", "condition2"],
    "importantDeadlines": ["deadline1", "deadline2"],
    "submissionInstructions": ["instruction1", "instruction2"],
    "specialNotes": ["note1", "note2"],
    "evaluationCriteria": ["Technical 60%", "Financial 40%"],
    "boqSummary": ["item1", "item2"],
    "projectDuration": "12 months",
    "warrantyRequirements": ["2 years comprehensive warranty"],
    "specialConditions": ["Omanization 35%"]
  },
  "compliance": {
    "overall": 85,
    "technical": 90,
    "commercial": 80,
    "legal": 95,
    "resources": 75,
    "complianceNotes": "Brief explanation of compliance scores"
  },
  "gapAnalysis": {
    "missingEngineers": [{"name": "Senior Fiber Engineer", "priority": "high", "department": "Engineering", "notes": "Required for fiber installation"}],
    "missingDocuments": [{"name": "Bank Guarantee", "priority": "high", "department": "Finance", "notes": "Required financial guarantee"}],
    "missingCertificates": [{"name": "ISO 27001", "priority": "medium", "department": "Quality", "notes": "Information security cert"}],
    "missingEquipment": [{"name": "Cable Blowing Machine", "priority": "medium", "department": "Procurement", "notes": "Required for installation"}],
    "missingExperience": [{"name": "Government project experience", "priority": "low", "department": "BD", "notes": "Preferred not mandatory"}],
    "missingFinancialDocs": [{"name": "2025 Audited Statement", "priority": "high", "department": "Finance", "notes": "Latest year required"}],
    "missingLicenses": [{"name": "Telecom License", "priority": "high", "department": "Legal", "notes": "Required for telecom work"}]
  },
  "recommendations": {
    "missingResources": ["Hire Senior Fiber Engineer", "Procure OTDR Tester"],
    "missingDocsToPrepare": ["Obtain Bank Guarantee from bank", "Renew ISO 9001 certificate"],
    "risksBeforeSubmission": ["Supply chain delays for equipment", "Engineer availability"],
    "suggestedActions": ["Start document preparation immediately", "Contact bank for guarantee"],
    "readinessScore": "82%",
    "probabilityOfSuccess": "75%",
    "recommendation": "Proceed with bid preparation after addressing critical gaps"
  },
  "gapItems": [
    {"category": "Engineers", "itemType": "missing_engineer", "itemName": "Senior Fiber Engineer", "description": "Required for fiber optic installation", "priority": "high", "department": "Engineering"},
    {"category": "Certificates", "itemType": "missing_certificate", "itemName": "ISO 27001", "description": "Information security management", "priority": "medium", "department": "Quality"},
    {"category": "Equipment", "itemType": "missing_equipment", "itemName": "Cable Blowing Machine", "description": "Required for cable installation", "priority": "medium", "department": "Procurement"},
    {"category": "Documents", "itemType": "missing_document", "itemName": "Bank Guarantee", "description": "Required financial guarantee", "priority": "high", "department": "Finance"}
  ],
  "tasks": [
    {"department": "Finance", "title": "Prepare Bank Guarantee", "description": "Obtain bank guarantee from financial institution", "priority": "high"},
    {"department": "HR", "title": "Assign Fiber Engineers", "description": "Identify and assign qualified fiber engineers", "priority": "high"},
    {"department": "Engineering", "title": "Review Technical Specifications", "description": "Detailed review of all technical requirements", "priority": "medium"},
    {"department": "Procurement", "title": "Arrange Required Equipment", "description": "Source and arrange all required equipment", "priority": "medium"},
    {"department": "Legal", "title": "Review Contract Conditions", "description": "Legal review of all contract terms", "priority": "high"},
    {"department": "Quality", "title": "Obtain ISO 27001 Certificate", "description": "Apply for ISO 27001 certification", "priority": "medium"},
    {"department": "BD", "title": "Prepare Technical Proposal", "description": "Develop comprehensive technical proposal", "priority": "high"}
  ]
}`;

  const handleAnalyze = async () => {
    if (!form?.tenderName?.trim()) { toast?.error('Tender Name is required'); return; }
    if (files?.length === 0) { toast?.error('Please upload at least one document'); return; }

    setAnalyzing(true);
    setAnalysisStep(0);
    setAnalysisResult(null);

    let step = 0;
    stepTimerRef.current = setInterval(() => {
      step += 1;
      if (step < ANALYSIS_STEPS?.length) setAnalysisStep(step);
    }, 1600);

    try {
      const tender = await createTender(form, user?.id);
      setSavedTenderId(tender?.id);

      const fileContents = await Promise.all(
        files?.map(async (f) => {
          const b64 = await fileToBase64(f);
          await createTenderDocument(tender?.id, {
            fileName: f?.name,
            fileType: ACCEPTED_TYPES?.[f?.type] || f?.type,
            fileSize: f?.size,
            fileData: b64,
          });
          return { name: f?.name, type: ACCEPTED_TYPES?.[f?.type] || f?.type, data: b64 };
        })
      );

      const systemPrompt = `You are an expert tender analyst specializing in compliance analysis for IT and engineering companies in Oman. 
Analyze the provided tender documents and extract all requirements in a structured JSON format.
Always respond with valid JSON only, no markdown, no code blocks.`;

      // Build content array: text prompt + all supported file attachments
      const pdfFiles = fileContents?.filter((f) => f?.type === 'PDF');
      const nonPdfFiles = fileContents?.filter((f) => f?.type !== 'PDF');

      // Build file attachment blocks for PDFs (OpenAI supports PDF natively)
      const pdfBlocks = pdfFiles?.slice(0, 3)?.map((f) => ({
        type: 'file',
        file: { file_data: f?.data, filename: f?.name },
      }));

      // For non-PDF files, describe them in the text prompt so AI knows they exist
      const nonPdfDescription = nonPdfFiles?.length > 0
        ? `\n\nAdditional uploaded files (${nonPdfFiles?.map((f) => `${f?.name} [${f?.type}]`)?.join(', ')}): These files contain tender data. Extract all relevant information based on the tender context provided above.`
        : '';

      const userContent = [
        { type: 'text', text: buildAIPrompt() + nonPdfDescription },
        ...pdfBlocks,
      ];

      const response = await getChatCompletion(
        'OPEN_AI',
        'gpt-4.1',
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userContent },
        ],
        { max_completion_tokens: 6000 }
      );

      clearInterval(stepTimerRef?.current);
      setAnalysisStep(ANALYSIS_STEPS?.length - 1);

      let parsed;
      try {
        const raw = response?.choices?.[0]?.message?.content || '{}';
        const cleaned = raw?.replace(/```json\n?/g, '')?.replace(/```\n?/g, '')?.trim();
        parsed = JSON.parse(cleaned);
      } catch {
        parsed = generateFallbackAnalysis(form);
      }

      // Normalize for backward compat
      const compliance = parsed?.compliance || {};
      const aiAnalysis = parsed?.aiAnalysis || {};

      await updateTender(tender?.id, {
        status: 'analyzed',
        complianceOverall: compliance?.overall || 85,
        complianceTechnical: compliance?.technical || 90,
        complianceCommercial: compliance?.commercial || 80,
        complianceLegal: compliance?.legal || 95,
        complianceResources: compliance?.resources || 75,
        analysisResult: parsed,
        executiveSummary: aiAnalysis?.executiveSummary || parsed?.executiveSummary || '',
      });

      if (parsed?.gapItems?.length > 0) {
        await createGapItems(tender?.id, parsed?.gapItems);
      }

      if (parsed?.tasks?.length > 0) {
        await createTenderTasks(tender?.id, parsed?.tasks);
      }

      setAnalysisResult(parsed);
      toast?.success('Tender analysis completed successfully!');
    } catch (err) {
      clearInterval(stepTimerRef?.current);
      console.error('Tender analysis error:', err);
      toast?.error(err?.message || 'Analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  useEffect(() => {
    return () => { if (stepTimerRef?.current) clearInterval(stepTimerRef?.current); };
  }, []);

  const resetForm = () => {
    setFiles([]);
    setForm({ tenderName: '', tenderNumber: '', clientName: '', projectName: '', closingDate: '', submissionDate: '', estimatedValue: '', tenderCategory: 'IT', department: 'Engineering', description: '' });
    setAnalysisResult(null);
    setSavedTenderId(null);
    setAnalysisStep(0);
  };

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Icon name="Brain" size={26} className="text-primary" />
              AI Tender Requirement Analyzer
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Upload tender documents — AI automatically extracts all requirements, analyzes compliance, and generates a full report</p>
          </div>
          {analysisResult && (
            <Button variant="outline" onClick={resetForm} className="gap-2">
              <Icon name="Plus" size={16} />
              New Analysis
            </Button>
          )}
        </div>

        {!analysisResult ? (
          <div className="space-y-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Upload Section */}
              <div className="space-y-4">
                <div className="bg-card border border-border rounded-2xl p-5">
                  <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="Upload" size={18} className="text-primary" />
                    Upload Tender Documents
                  </h2>
                  <div
                    onDragOver={(e) => { e?.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleFileDrop}
                    onClick={() => fileInputRef?.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200
                      ${dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'}`}
                  >
                    <Icon name="FileUp" size={36} className="mx-auto text-muted-foreground mb-3" />
                    <p className="font-medium text-foreground">Drop files here or click to browse</p>
                    <p className="text-xs text-muted-foreground mt-1">PDF, DOCX, XLSX, ZIP supported</p>
                    <div className="flex justify-center gap-2 mt-3">
                      {['PDF', 'DOCX', 'XLSX', 'ZIP']?.map((fmt) => (
                        <span key={fmt} className="text-xs bg-muted/60 text-muted-foreground px-2 py-0.5 rounded-full">{fmt}</span>
                      ))}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.docx,.doc,.xlsx,.xls,.zip"
                      className="hidden"
                      onChange={(e) => addFiles(Array.from(e?.target?.files || []))}
                    />
                  </div>

                  {files?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {files?.map((f) => (
                        <div key={f?.name} className="flex items-center justify-between bg-muted/40 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <Icon name="FileText" size={16} className="text-primary flex-shrink-0" />
                            <span className="text-sm truncate text-foreground">{f?.name}</span>
                            <span className="text-xs text-muted-foreground flex-shrink-0">{formatFileSize(f?.size)}</span>
                          </div>
                          <button onClick={() => removeFile(f?.name)} className="text-muted-foreground hover:text-error ml-2">
                            <Icon name="X" size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* AI Capabilities */}
                  <div className="mt-4 bg-primary/5 border border-primary/20 rounded-xl p-3">
                    <p className="text-xs font-semibold text-primary mb-2 flex items-center gap-1.5">
                      <Icon name="Sparkles" size={13} />
                      AI Auto-Extraction
                    </p>
                    <div className="grid grid-cols-2 gap-1">
                      {['Basic Information', 'Technical Requirements', 'Commercial Terms', 'Legal Requirements', 'HSE Requirements', 'Gap Analysis', 'Compliance Score', 'AI Recommendations']?.map((item) => (
                        <div key={item} className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Icon name="Check" size={11} className="text-success" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tender Details Form */}
              <div className="bg-card border border-border rounded-2xl p-5">
                <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="ClipboardList" size={18} className="text-primary" />
                  Tender Details
                  {extractingDetails && (
                    <span className="ml-auto flex items-center gap-1.5 text-xs text-primary font-normal">
                      <Icon name="Loader2" size={13} className="animate-spin" />
                      Auto-extracting from document...
                    </span>
                  )}
                </h2>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Tender Name *</label>
                      <input name="tenderName" value={form?.tenderName} onChange={handleFormChange}
                        className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        placeholder="e.g. Fiber Network Expansion" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Tender Number</label>
                      <input name="tenderNumber" value={form?.tenderNumber} onChange={handleFormChange}
                        className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        placeholder="e.g. TND-2026-001" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Client Name</label>
                      <input name="clientName" value={form?.clientName} onChange={handleFormChange}
                        className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        placeholder="Client organization" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Project Name</label>
                      <input name="projectName" value={form?.projectName} onChange={handleFormChange}
                        className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        placeholder="Project title" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Closing Date</label>
                      <input type="date" name="closingDate" value={form?.closingDate} onChange={handleFormChange}
                        className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Submission Date</label>
                      <input type="date" name="submissionDate" value={form?.submissionDate} onChange={handleFormChange}
                        className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Estimated Value (OMR)</label>
                      <input type="number" name="estimatedValue" value={form?.estimatedValue} onChange={handleFormChange}
                        className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        placeholder="0.000" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Category</label>
                      <select name="tenderCategory" value={form?.tenderCategory} onChange={handleFormChange}
                        className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
                        {CATEGORIES?.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Department</label>
                    <select name="department" value={form?.department} onChange={handleFormChange}
                      className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
                      {DEPARTMENTS?.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Description</label>
                    <textarea name="description" value={form?.description} onChange={handleFormChange} rows={3}
                      className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                      placeholder="Brief description of the tender..." />
                  </div>
                </div>
              </div>
            </div>

            {/* Analyze Button */}
            <div>
              <Button
                onClick={handleAnalyze}
                disabled={analyzing || !form?.tenderName?.trim() || files?.length === 0}
                className="w-full h-12 text-base font-semibold gap-2"
              >
                {analyzing ? (
                  <>
                    <Icon name="Loader2" size={20} className="animate-spin" />
                    Analyzing... {ANALYSIS_STEPS?.[analysisStep]}
                  </>
                ) : (
                  <>
                    <Icon name="Brain" size={20} />
                    Analyze Tender with AI
                  </>
                )}
              </Button>
              {analyzing && (
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>{ANALYSIS_STEPS?.[analysisStep]}</span>
                    <span>{Math.round(((analysisStep + 1) / ANALYSIS_STEPS?.length) * 100)}%</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-700 rounded-full"
                      style={{ width: `${((analysisStep + 1) / ANALYSIS_STEPS?.length) * 100}%` }}
                    />
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {ANALYSIS_STEPS?.map((step, i) => (
                      <div key={i} className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full transition-all ${
                        i < analysisStep ? 'bg-success/15 text-success' :
                        i === analysisStep ? 'bg-primary/15 text-primary': 'bg-muted/40 text-muted-foreground'
                      }`}>
                        {i < analysisStep && <Icon name="Check" size={10} />}
                        {i === analysisStep && <Icon name="Loader2" size={10} className="animate-spin" />}
                        {step?.replace('...', '')}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <AnalysisResultView result={analysisResult} tenderId={savedTenderId} navigate={navigate} onReset={resetForm} />
        )}
      </div>
    </MainLayout>
  );
};

// ─── Sub-components ────────────────────────────────────────────────────────────

const ComplianceBar = ({ label, value }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-semibold ${value >= 90 ? 'text-success' : value >= 75 ? 'text-warning' : 'text-error'}`}>{value}%</span>
    </div>
    <div className="h-2 bg-muted rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-all duration-700 ${value >= 90 ? 'bg-success' : value >= 75 ? 'bg-warning' : 'bg-error'}`}
        style={{ width: `${value}%` }} />
    </div>
  </div>
);

const SectionList = ({ title, icon, items, color = 'text-primary', maxItems = 8 }) => {
  const [expanded, setExpanded] = useState(false);
  if (!items?.length) return null;
  const display = expanded ? items : items?.slice(0, maxItems);
  return (
    <div>
      <h4 className={`font-semibold text-sm mb-2 flex items-center gap-1.5 ${color}`}>
        <Icon name={icon} size={15} />
        {title}
      </h4>
      <ul className="space-y-1">
        {display?.map((item, i) => (
          <li key={i} className="text-sm text-foreground flex items-start gap-2">
            <span className="text-muted-foreground mt-0.5 flex-shrink-0">•</span>
            <span>{typeof item === 'object' ? (item?.role ? `${item?.role} ×${item?.count || 1} — ${item?.experience || ''}` : JSON.stringify(item)) : item}</span>
          </li>
        ))}
      </ul>
      {items?.length > maxItems && (
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-primary mt-1 hover:underline">
          {expanded ? 'Show less' : `+${items?.length - maxItems} more`}
        </button>
      )}
    </div>
  );
};

const InfoRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-border/50 last:border-0">
      <span className="text-xs text-muted-foreground w-36 flex-shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-foreground font-medium">{value}</span>
    </div>
  );
};

const GapSection = ({ title, icon, items, color }) => {
  if (!items?.length) return null;
  return (
    <div className="bg-card border border-border rounded-2xl p-4">
      <h4 className={`font-semibold text-sm mb-3 flex items-center gap-1.5 ${color}`}>
        <Icon name={icon} size={15} />
        {title} ({items?.length})
      </h4>
      <div className="space-y-2">
        {items?.map((item, i) => (
          <div key={i} className="flex items-start justify-between gap-3 bg-muted/30 rounded-lg px-3 py-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">{item?.name || item?.itemName}</p>
              {item?.notes && <p className="text-xs text-muted-foreground mt-0.5">{item?.notes}</p>}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {item?.department && (
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{item?.department}</span>
              )}
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                item?.priority === 'high' || item?.priority === 'critical' ? 'bg-error/15 text-error' :
                item?.priority === 'medium' ? 'bg-warning/15 text-warning' : 'bg-muted text-muted-foreground'
              }`}>{item?.priority || 'medium'}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AnalysisResultView = ({ result, tenderId, navigate, onReset }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const compliance = result?.compliance || {};
  const basicInfo = result?.basicInfo || {};
  const projectInfo = result?.projectInfo || {};
  const techReq = result?.technicalRequirements || {};
  const commReq = result?.commercialRequirements || {};
  const legalReq = result?.legalRequirements || {};
  const hseReq = result?.hseRequirements || {};
  const aiAnalysis = result?.aiAnalysis || {};
  const gapAnalysis = result?.gapAnalysis || {};
  const recommendations = result?.recommendations || {};
  const requiredDocs = result?.requiredDocuments || REQUIRED_DOCS_CHECKLIST?.map((name) => ({ name, required: true, notes: '' }));

  const TABS = [
    { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
    { id: 'basic', label: 'Basic Info', icon: 'Info' },
    { id: 'project', label: 'Project Info', icon: 'FolderOpen' },
    { id: 'technical', label: 'Technical', icon: 'Settings' },
    { id: 'commercial', label: 'Commercial', icon: 'DollarSign' },
    { id: 'legal', label: 'Legal', icon: 'Scale' },
    { id: 'hse', label: 'HSE', icon: 'ShieldAlert' },
    { id: 'documents', label: 'Documents', icon: 'FileCheck' },
    { id: 'gaps', label: 'Gap Analysis', icon: 'AlertTriangle' },
    { id: 'recommendations', label: 'Recommendations', icon: 'Lightbulb' },
    { id: 'summary', label: 'Executive Summary', icon: 'FileText' },
  ];

  const handleExport = (type) => {
    if (type === 'pdf') {
      window.print();
      toast?.success('Opening print dialog for PDF export');
    } else {
      toast?.success(`${type?.toUpperCase()} export initiated`);
    }
  };

  const totalGaps = (gapAnalysis?.missingEngineers?.length || 0) +
    (gapAnalysis?.missingDocuments?.length || 0) +
    (gapAnalysis?.missingCertificates?.length || 0) +
    (gapAnalysis?.missingEquipment?.length || 0) +
    (gapAnalysis?.missingExperience?.length || 0) +
    (gapAnalysis?.missingFinancialDocs?.length || 0) +
    (gapAnalysis?.missingLicenses?.length || 0);

  return (
    <div className="space-y-5">
      {/* Success Banner */}
      <div className="bg-success/10 border border-success/30 rounded-2xl p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
          <Icon name="CheckCircle2" size={22} className="text-success" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-foreground">AI Analysis Complete</p>
          <p className="text-sm text-muted-foreground">All tender requirements extracted and analyzed automatically</p>
        </div>
        <div className="flex gap-2">
          {tenderId && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/tender-compliance/${tenderId}`)} className="gap-1.5">
              <Icon name="BarChart2" size={14} />
              Full Report
            </Button>
          )}
          <div className="relative group">
            <Button variant="outline" size="sm" className="gap-1.5">
              <Icon name="Download" size={14} />
              Export
            </Button>
            <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-xl shadow-lg p-1 z-10 hidden group-hover:block min-w-[120px]">
              {['PDF', 'Excel', 'Word']?.map((fmt) => (
                <button key={fmt} onClick={() => handleExport(fmt?.toLowerCase())}
                  className="w-full text-left text-sm px-3 py-1.5 hover:bg-muted/50 rounded-lg text-foreground flex items-center gap-2">
                  <Icon name={fmt === 'PDF' ? 'FileText' : fmt === 'Excel' ? 'Table' : 'FileType'} size={13} />
                  {fmt}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Tab Navigation */}
      <div className="flex gap-1 bg-muted/30 rounded-xl p-1 overflow-x-auto">
        {TABS?.map((tab) => (
          <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap
              ${activeTab === tab?.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            <Icon name={tab?.icon} size={13} />
            {tab?.label}
          </button>
        ))}
      </div>
      {/* ── DASHBOARD TAB ── */}
      {activeTab === 'dashboard' && (
        <div className="space-y-5">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Compliance Score', value: `${compliance?.overall || 0}%`, icon: 'ShieldCheck', color: 'text-success', bg: 'bg-success/10' },
              { label: 'Gap Items', value: totalGaps, icon: 'AlertTriangle', color: 'text-warning', bg: 'bg-warning/10' },
              { label: 'Tasks Generated', value: result?.tasks?.length || 0, icon: 'ListTodo', color: 'text-primary', bg: 'bg-primary/10' },
              { label: 'Readiness Score', value: recommendations?.readinessScore || aiAnalysis?.estimatedReadiness || '—', icon: 'Target', color: 'text-blue-400', bg: 'bg-blue-500/10' },
            ]?.map((stat) => (
              <div key={stat?.label} className="bg-card border border-border rounded-xl p-4">
                <div className={`w-9 h-9 rounded-xl ${stat?.bg} flex items-center justify-center mb-3`}>
                  <Icon name={stat?.icon} size={18} className={stat?.color} />
                </div>
                <div className={`text-2xl font-bold ${stat?.color}`}>{stat?.value}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{stat?.label}</div>
              </div>
            ))}
          </div>

          {/* Compliance + Closing Date */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="ShieldCheck" size={18} className="text-primary" />
                Compliance Analysis
              </h3>
              <div className="text-center mb-5">
                <div className={`text-5xl font-bold ${(compliance?.overall || 0) >= 90 ? 'text-success' : (compliance?.overall || 0) >= 75 ? 'text-warning' : 'text-error'}`}>
                  {compliance?.overall || 0}%
                </div>
                <div className="text-sm text-muted-foreground mt-1">Overall Compliance</div>
              </div>
              <div className="space-y-3">
                <ComplianceBar label="Technical" value={compliance?.technical || 0} />
                <ComplianceBar label="Commercial" value={compliance?.commercial || 0} />
                <ComplianceBar label="Legal" value={compliance?.legal || 0} />
                <ComplianceBar label="Resources" value={compliance?.resources || 0} />
              </div>
              {compliance?.complianceNotes && (
                <p className="text-xs text-muted-foreground mt-3 bg-muted/30 rounded-lg p-2">{compliance?.complianceNotes}</p>
              )}
            </div>

            <div className="space-y-4">
              {/* Tender Info Summary */}
              <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Info" size={18} className="text-primary" />
                  Tender Overview
                </h3>
                <div className="space-y-0.5">
                  <InfoRow label="Tender Type" value={basicInfo?.tenderType} />
                  <InfoRow label="Contract Duration" value={basicInfo?.contractDuration || aiAnalysis?.projectDuration} />
                  <InfoRow label="Bid Validity" value={basicInfo?.bidValidity} />
                  <InfoRow label="Bid Bond" value={commReq?.bidBond} />
                  <InfoRow label="Performance Bond" value={commReq?.performanceBond} />
                  <InfoRow label="Payment Terms" value={commReq?.paymentTerms} />
                </div>
              </div>

              {/* AI Risk Score */}
              <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Brain" size={18} className="text-primary" />
                  AI Assessment
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center bg-muted/30 rounded-xl p-3">
                    <div className="text-xl font-bold text-success">{recommendations?.readinessScore || '—'}</div>
                    <div className="text-xs text-muted-foreground">Readiness Score</div>
                  </div>
                  <div className="text-center bg-muted/30 rounded-xl p-3">
                    <div className="text-xl font-bold text-primary">{recommendations?.probabilityOfSuccess || '—'}</div>
                    <div className="text-xs text-muted-foreground">Success Probability</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Risks */}
          {aiAnalysis?.keyRisks?.length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="AlertOctagon" size={18} className="text-error" />
                Key Risks
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {aiAnalysis?.keyRisks?.map((risk, i) => (
                  <div key={i} className="flex items-start gap-2 bg-error/5 border border-error/20 rounded-lg px-3 py-2">
                    <Icon name="AlertCircle" size={14} className="text-error mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">{risk}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Generated Tasks */}
          {result?.tasks?.length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="ListTodo" size={18} className="text-primary" />
                Auto-Generated Tasks ({result?.tasks?.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {result?.tasks?.map((task, i) => (
                  <div key={i} className="bg-muted/30 border border-border rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">{task?.department}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        task?.priority === 'high' || task?.priority === 'critical' ? 'bg-error/15 text-error' :
                        task?.priority === 'medium' ? 'bg-warning/15 text-warning' : 'bg-muted text-muted-foreground'
                      }`}>{task?.priority}</span>
                    </div>
                    <p className="text-sm font-medium text-foreground mt-1">{task?.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{task?.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {/* ── BASIC INFO TAB ── */}
      {activeTab === 'basic' && (
        <div className="bg-card border border-border rounded-2xl p-6">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Info" size={18} className="text-primary" />
            Basic Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <div>
              <InfoRow label="Tender Name" value={basicInfo?.tenderName} />
              <InfoRow label="Tender Number" value={basicInfo?.tenderNumber} />
              <InfoRow label="Client Name" value={basicInfo?.clientName} />
              <InfoRow label="Project Name" value={basicInfo?.projectName} />
              <InfoRow label="Tender Type" value={basicInfo?.tenderType} />
              <InfoRow label="Tender Category" value={basicInfo?.tenderCategory} />
            </div>
            <div>
              <InfoRow label="Country" value={basicInfo?.country} />
              <InfoRow label="City" value={basicInfo?.city} />
              <InfoRow label="Issue Date" value={basicInfo?.issueDate} />
              <InfoRow label="Closing Date" value={basicInfo?.closingDate} />
              <InfoRow label="Submission Date" value={basicInfo?.submissionDate} />
              <InfoRow label="Tender Value" value={basicInfo?.tenderValue} />
              <InfoRow label="Contract Duration" value={basicInfo?.contractDuration} />
              <InfoRow label="Bid Validity" value={basicInfo?.bidValidity} />
            </div>
          </div>
        </div>
      )}
      {/* ── PROJECT INFO TAB ── */}
      {activeTab === 'project' && (
        <div className="space-y-4">
          {projectInfo?.projectDescription && (
            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                <Icon name="FileText" size={16} className="text-primary" />
                Project Description
              </h3>
              <p className="text-sm text-foreground leading-relaxed">{projectInfo?.projectDescription}</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-2xl p-4">
              <SectionList title="Scope of Work" icon="Target" items={projectInfo?.scopeOfWork} />
            </div>
            <div className="bg-card border border-border rounded-2xl p-4">
              <SectionList title="Deliverables" icon="Package" items={projectInfo?.deliverables} color="text-blue-400" />
            </div>
            <div className="bg-card border border-border rounded-2xl p-4">
              <SectionList title="Technical Scope" icon="Settings" items={projectInfo?.technicalScope} color="text-purple-400" />
            </div>
            <div className="bg-card border border-border rounded-2xl p-4">
              <SectionList title="Commercial Scope" icon="DollarSign" items={projectInfo?.commercialScope} color="text-success" />
            </div>
            <div className="bg-card border border-border rounded-2xl p-4">
              <SectionList title="Project Objectives" icon="Flag" items={projectInfo?.projectObjectives} color="text-warning" />
            </div>
            {projectInfo?.expectedTimeline && (
              <div className="bg-card border border-border rounded-2xl p-4">
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-1.5 text-teal-400">
                  <Icon name="Clock" size={15} />
                  Expected Timeline
                </h4>
                <p className="text-sm text-foreground">{projectInfo?.expectedTimeline}</p>
              </div>
            )}
          </div>
        </div>
      )}
      {/* ── TECHNICAL TAB ── */}
      {activeTab === 'technical' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-2xl p-4">
            <SectionList title="Required Engineers" icon="Users" items={techReq?.requiredEngineers} />
          </div>
          <div className="bg-card border border-border rounded-2xl p-4">
            <SectionList title="Required Certifications" icon="Award" items={techReq?.requiredCertifications} color="text-success" />
          </div>
          <div className="bg-card border border-border rounded-2xl p-4">
            <SectionList title="Required Equipment" icon="Wrench" items={techReq?.requiredEquipment} color="text-orange-400" />
          </div>
          <div className="bg-card border border-border rounded-2xl p-4">
            <SectionList title="Required Vehicles" icon="Truck" items={techReq?.requiredVehicles} color="text-blue-400" />
          </div>
          <div className="bg-card border border-border rounded-2xl p-4">
            <SectionList title="Required Software" icon="Monitor" items={techReq?.requiredSoftware} color="text-purple-400" />
          </div>
          <div className="bg-card border border-border rounded-2xl p-4">
            <SectionList title="Required Experience" icon="Briefcase" items={techReq?.requiredExperience} color="text-teal-400" />
          </div>
          <div className="bg-card border border-border rounded-2xl p-4">
            <SectionList title="Required Standards" icon="BookOpen" items={techReq?.requiredStandards} color="text-indigo-400" />
          </div>
          <div className="bg-card border border-border rounded-2xl p-4">
            <SectionList title="Required Methodology" icon="GitBranch" items={techReq?.requiredMethodology} color="text-pink-400" />
          </div>
          <div className="bg-card border border-border rounded-2xl p-4 md:col-span-2">
            <SectionList title="Required Materials" icon="Package" items={techReq?.requiredMaterials} color="text-amber-400" />
          </div>
        </div>
      )}
      {/* ── COMMERCIAL TAB ── */}
      {activeTab === 'commercial' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-2xl p-6">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="DollarSign" size={18} className="text-primary" />
              Commercial Requirements
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              <div>
                <InfoRow label="Bid Bond" value={commReq?.bidBond} />
                <InfoRow label="Performance Bond" value={commReq?.performanceBond} />
                <InfoRow label="Bank Guarantee" value={commReq?.bankGuarantee} />
                <InfoRow label="Payment Terms" value={commReq?.paymentTerms} />
                <InfoRow label="Commercial Proposal" value={commReq?.commercialProposal} />
              </div>
              <div className="space-y-3">
                <SectionList title="Financial Requirements" icon="Banknote" items={commReq?.financialRequirements} color="text-success" />
                <SectionList title="Tax Requirements" icon="Receipt" items={commReq?.taxRequirements} color="text-warning" />
                <SectionList title="Pricing Instructions" icon="Tag" items={commReq?.pricingInstructions} color="text-blue-400" />
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── LEGAL TAB ── */}
      {activeTab === 'legal' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-2xl p-6">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Scale" size={18} className="text-primary" />
              Legal Requirements
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              <div>
                <InfoRow label="Company Registration" value={legalReq?.companyRegistration} />
                <InfoRow label="Chamber Certificate" value={legalReq?.chamberCertificate} />
                <InfoRow label="VAT Certificate" value={legalReq?.vatCertificate} />
                <InfoRow label="NDA" value={legalReq?.nda} />
              </div>
              <div className="space-y-3">
                <SectionList title="Required Licenses" icon="FileKey" items={legalReq?.licenses} color="text-purple-400" />
                <SectionList title="ISO Certificates" icon="Award" items={legalReq?.isoCertificates} color="text-success" />
                <SectionList title="Contract Conditions" icon="FileSignature" items={legalReq?.contractConditions} color="text-warning" />
                <SectionList title="Compliance Requirements" icon="CheckSquare" items={legalReq?.complianceRequirements} color="text-blue-400" />
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── HSE TAB ── */}
      {activeTab === 'hse' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-2xl p-6">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="ShieldAlert" size={18} className="text-primary" />
              HSE Requirements
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              <div>
                <InfoRow label="HSE Plan" value={hseReq?.hsePlan} />
                <InfoRow label="Risk Assessment" value={hseReq?.riskAssessment} />
              </div>
              <div className="space-y-3">
                <SectionList title="Safety Certificates" icon="Award" items={hseReq?.safetyCertificates} color="text-success" />
                <SectionList title="PPE Requirements" icon="HardHat" items={hseReq?.ppeRequirements} color="text-warning" />
                <SectionList title="Environmental Requirements" icon="Leaf" items={hseReq?.environmentalRequirements} color="text-teal-400" />
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── DOCUMENTS TAB ── */}
      {activeTab === 'documents' && (
        <div className="bg-card border border-border rounded-2xl p-5">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="FileCheck" size={18} className="text-primary" />
            Required Documents Checklist
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {requiredDocs?.map((doc, i) => {
              const docName = typeof doc === 'string' ? doc : doc?.name;
              const required = typeof doc === 'string' ? true : doc?.required !== false;
              const notes = typeof doc === 'object' ? doc?.notes : '';
              return (
                <div key={i} className={`flex items-start gap-3 rounded-xl px-4 py-3 border ${required ? 'bg-muted/30 border-border' : 'bg-muted/10 border-border/50'}`}>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${required ? 'border-primary' : 'border-muted-foreground/40'}`}>
                    {required && <Icon name="Check" size={11} className="text-primary" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${required ? 'text-foreground' : 'text-muted-foreground'}`}>{docName}</p>
                    {notes && <p className="text-xs text-muted-foreground mt-0.5">{notes}</p>}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${required ? 'bg-error/10 text-error' : 'bg-muted text-muted-foreground'}`}>
                    {required ? 'Required' : 'Optional'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* ── GAP ANALYSIS TAB ── */}
      {activeTab === 'gaps' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Missing Engineers', count: gapAnalysis?.missingEngineers?.length || 0, icon: 'Users', color: 'text-error' },
              { label: 'Missing Documents', count: gapAnalysis?.missingDocuments?.length || 0, icon: 'FileX', color: 'text-warning' },
              { label: 'Missing Certificates', count: gapAnalysis?.missingCertificates?.length || 0, icon: 'Award', color: 'text-orange-400' },
              { label: 'Missing Equipment', count: gapAnalysis?.missingEquipment?.length || 0, icon: 'Wrench', color: 'text-blue-400' },
            ]?.map((stat) => (
              <div key={stat?.label} className="bg-card border border-border rounded-xl p-3 text-center">
                <Icon name={stat?.icon} size={20} className={`${stat?.color} mx-auto mb-1`} />
                <div className={`text-2xl font-bold ${stat?.color}`}>{stat?.count}</div>
                <div className="text-xs text-muted-foreground">{stat?.label}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <GapSection title="Missing Engineers" icon="Users" items={gapAnalysis?.missingEngineers} color="text-error" />
            <GapSection title="Missing Documents" icon="FileX" items={gapAnalysis?.missingDocuments} color="text-warning" />
            <GapSection title="Missing Certificates" icon="Award" items={gapAnalysis?.missingCertificates} color="text-orange-400" />
            <GapSection title="Missing Equipment" icon="Wrench" items={gapAnalysis?.missingEquipment} color="text-blue-400" />
            <GapSection title="Missing Experience" icon="Briefcase" items={gapAnalysis?.missingExperience} color="text-purple-400" />
            <GapSection title="Missing Financial Docs" icon="Banknote" items={gapAnalysis?.missingFinancialDocs} color="text-success" />
            <GapSection title="Missing Licenses" icon="FileKey" items={gapAnalysis?.missingLicenses} color="text-teal-400" />
          </div>
          {totalGaps === 0 && (
            <div className="text-center py-12 bg-card border border-border rounded-2xl">
              <Icon name="CheckCircle2" size={40} className="mx-auto text-success mb-3" />
              <p className="text-foreground font-medium">No gaps identified</p>
              <p className="text-sm text-muted-foreground">All requirements appear to be met</p>
            </div>
          )}
        </div>
      )}
      {/* ── RECOMMENDATIONS TAB ── */}
      {activeTab === 'recommendations' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Target" size={16} className="text-primary" />
                AI Assessment
              </h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="text-center bg-success/10 rounded-xl p-3">
                  <div className="text-2xl font-bold text-success">{recommendations?.readinessScore || '—'}</div>
                  <div className="text-xs text-muted-foreground">Readiness Score</div>
                </div>
                <div className="text-center bg-primary/10 rounded-xl p-3">
                  <div className="text-2xl font-bold text-primary">{recommendations?.probabilityOfSuccess || '—'}</div>
                  <div className="text-xs text-muted-foreground">Success Probability</div>
                </div>
              </div>
              {recommendations?.recommendation && (
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-3">
                  <p className="text-xs font-semibold text-primary mb-1">Overall Recommendation</p>
                  <p className="text-sm text-foreground">{recommendations?.recommendation}</p>
                </div>
              )}
            </div>
            <div className="space-y-3">
              <div className="bg-card border border-border rounded-2xl p-4">
                <SectionList title="Missing Resources to Arrange" icon="Package" items={recommendations?.missingResources} color="text-warning" />
              </div>
              <div className="bg-card border border-border rounded-2xl p-4">
                <SectionList title="Documents to Prepare" icon="FileText" items={recommendations?.missingDocsToPrepare} color="text-blue-400" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-2xl p-4">
              <SectionList title="Risks Before Submission" icon="AlertTriangle" items={recommendations?.risksBeforeSubmission} color="text-error" />
            </div>
            <div className="bg-card border border-border rounded-2xl p-4">
              <SectionList title="Suggested Actions" icon="Lightbulb" items={recommendations?.suggestedActions} color="text-success" />
            </div>
          </div>
        </div>
      )}
      {/* ── EXECUTIVE SUMMARY TAB ── */}
      {activeTab === 'summary' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="FileText" size={20} className="text-primary" />
              <h3 className="font-semibold text-foreground text-lg">Executive Summary</h3>
            </div>
            <p className="text-foreground leading-relaxed mb-4">{aiAnalysis?.executiveSummary || result?.executiveSummary || 'No summary available.'}</p>
            {aiAnalysis?.scopeSummary && (
              <div className="bg-muted/30 rounded-xl p-4 mb-4">
                <p className="text-xs font-semibold text-muted-foreground mb-1">Scope Summary</p>
                <p className="text-sm text-foreground">{aiAnalysis?.scopeSummary}</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <SectionList title="Major Requirements" icon="ClipboardList" items={aiAnalysis?.majorRequirements} />
              </div>
              <div>
                <SectionList title="Critical Conditions" icon="AlertOctagon" items={aiAnalysis?.criticalConditions} color="text-error" />
              </div>
              <div>
                <SectionList title="Important Deadlines" icon="Calendar" items={aiAnalysis?.importantDeadlines} color="text-warning" />
              </div>
              <div>
                <SectionList title="Submission Instructions" icon="Send" items={aiAnalysis?.submissionInstructions} color="text-blue-400" />
              </div>
              <div>
                <SectionList title="Special Notes" icon="StickyNote" items={aiAnalysis?.specialNotes} color="text-purple-400" />
              </div>
              <div>
                <SectionList title="Evaluation Criteria" icon="Star" items={aiAnalysis?.evaluationCriteria} color="text-success" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function generateFallbackAnalysis(form) {
  return {
    basicInfo: {
      tenderName: form?.tenderName,
      tenderNumber: form?.tenderNumber || 'TND-2026-001',
      clientName: form?.clientName || 'Government Entity',
      projectName: form?.projectName || form?.tenderName,
      tenderType: 'Open Tender',
      tenderCategory: form?.tenderCategory,
      country: 'Oman',
      city: 'Muscat',
      issueDate: null,
      closingDate: form?.closingDate || null,
      submissionDate: form?.submissionDate || null,
      tenderValue: form?.estimatedValue ? `OMR ${form?.estimatedValue}` : null,
      contractDuration: '12 months',
      bidValidity: '90 days',
    },
    projectInfo: {
      scopeOfWork: ['Design and implementation of network infrastructure', 'Installation and commissioning', 'Testing and acceptance', 'Training and handover'],
      projectDescription: `This ${form?.tenderCategory} project requires comprehensive technical and commercial preparation.`,
      deliverables: ['Completed network infrastructure', 'As-built drawings', 'Test reports', 'Training completion certificates'],
      technicalScope: ['Fiber optic cabling', 'Network switches installation', 'Remote monitoring setup'],
      commercialScope: ['Supply of materials', 'Installation services', 'Maintenance support'],
      projectObjectives: ['Improve network connectivity', 'Ensure redundancy', 'Meet SLA requirements'],
      expectedTimeline: '12 months from contract award',
    },
    technicalRequirements: {
      requiredEngineers: [{ role: 'Senior Network Engineer', count: 2, experience: '5 years', specialization: 'Fiber Optics' }, { role: 'Project Manager', count: 1, experience: '7 years', specialization: 'IT Projects' }],
      requiredCertifications: ['ISO 9001:2015', 'ISO 27001', 'CISA Certification'],
      requiredEquipment: ['Cable Blowing Machine', 'OTDR Tester', 'Network Analyzer'],
      requiredVehicles: ['Pickup Truck', 'Crane (if required)'],
      requiredSoftware: ['AutoCAD', 'MS Project', 'Network Management System'],
      requiredExperience: ['Minimum 5 years in similar projects', 'Previous government projects preferred'],
      requiredStandards: ['IEC 60793', 'TIA-568', 'ISO/IEC 11801'],
      requiredMethodology: ['PRINCE2 or PMP methodology'],
      requiredMaterials: ['Fiber optic cable Cat6A', 'Network switches 48-port', 'Patch panels'],
    },
    commercialRequirements: {
      bidBond: '2% of tender value',
      performanceBond: '10% of contract value',
      bankGuarantee: 'Required from approved bank',
      paymentTerms: '30 days net from invoice',
      commercialProposal: 'Sealed envelope, separate from technical',
      financialRequirements: ['Audited financial statements 2023-2024', 'Minimum turnover OMR 500,000'],
      taxRequirements: ['VAT registration required', 'Tax compliance certificate'],
      pricingInstructions: ['Fixed price contract', 'No price escalation', 'Prices in OMR'],
    },
    legalRequirements: {
      companyRegistration: 'Valid Oman commercial registration required',
      chamberCertificate: 'Required - Oman Chamber of Commerce',
      vatCertificate: 'Required',
      licenses: ['Telecom license from TRA', 'Civil works license'],
      isoCertificates: ['ISO 9001:2015', 'ISO 27001'],
      nda: 'Required before document access',
      contractConditions: ['FIDIC contract', 'Liquidated damages 0.5% per week', 'Dispute resolution via arbitration'],
      complianceRequirements: ['Omanization requirement 35%', 'Local content preference'],
    },
    hseRequirements: {
      hsePlan: 'Required - submit with technical proposal',
      safetyCertificates: ['NEBOSH', 'IOSH'],
      ppeRequirements: ['Hard hat', 'Safety vest', 'Safety boots', 'Safety glasses'],
      riskAssessment: 'Required before site work',
      environmentalRequirements: ['Environmental impact assessment', 'Waste management plan'],
    },
    requiredDocuments: REQUIRED_DOCS_CHECKLIST?.map((name) => ({ name, required: true, notes: '' })),
    aiAnalysis: {
      executiveSummary: `This ${form?.tenderCategory} tender from ${form?.clientName || 'the client'} requires comprehensive technical and commercial preparation. The overall compliance stands at 82% with key gaps in resource availability and documentation. Immediate action is required to address missing certifications and engineer assignments.`,
      scopeSummary: 'Network infrastructure design, supply, installation, and commissioning project.',
      keyRisks: ['Supply chain delays for specialized equipment', 'Engineer availability and mobilization', 'Site access restrictions', 'Regulatory approval timelines'],
      majorRequirements: ['ISO 9001:2015 certification', 'Bank guarantee 10%', 'Omanization 35%', 'FIDIC contract compliance'],
      criticalConditions: ['Liquidated damages 0.5% per week', 'Performance bond 10%', 'Strict submission deadline'],
      importantDeadlines: [`Closing Date: ${form?.closingDate || 'TBD'}`, `Submission: ${form?.submissionDate || 'TBD'}`],
      submissionInstructions: ['Hard copy and soft copy required', 'Sealed envelope', 'Deadline strictly enforced', 'Technical and commercial separate'],
      specialNotes: ['Omanization requirement applies', 'Local content preference given', 'Site visit mandatory'],
      evaluationCriteria: ['Technical score 60%', 'Financial score 40%'],
      projectDuration: '12 months',
      warrantyRequirements: ['2 years comprehensive warranty', 'On-site support included'],
      specialConditions: ['Omanization requirement 35%', 'Local content preference'],
    },
    compliance: { overall: 82, technical: 88, commercial: 78, legal: 92, resources: 70, complianceNotes: 'Good technical compliance. Resource gaps in specialized engineers and equipment.' },
    gapAnalysis: {
      missingEngineers: [{ name: 'Senior Fiber Engineer', priority: 'high', department: 'Engineering', notes: 'Required for fiber optic installation' }],
      missingDocuments: [{ name: 'Bank Guarantee', priority: 'high', department: 'Finance', notes: 'Required financial guarantee document' }],
      missingCertificates: [{ name: 'ISO 27001', priority: 'medium', department: 'Quality', notes: 'Information security management certificate' }],
      missingEquipment: [{ name: 'Cable Blowing Machine', priority: 'medium', department: 'Procurement', notes: 'Required for cable installation' }],
      missingExperience: [],
      missingFinancialDocs: [{ name: '2025 Audited Statement', priority: 'high', department: 'Finance', notes: 'Latest year required' }],
      missingLicenses: [{ name: 'Telecom License (TRA)', priority: 'high', department: 'Legal', notes: 'Required for telecom work in Oman' }],
    },
    recommendations: {
      missingResources: ['Hire Senior Fiber Engineer immediately', 'Procure OTDR Tester and Cable Blowing Machine'],
      missingDocsToPrepare: ['Obtain Bank Guarantee from bank', 'Apply for ISO 27001 certification', 'Prepare 2025 audited financial statements'],
      risksBeforeSubmission: ['Equipment procurement lead time may exceed deadline', 'ISO 27001 certification takes 3-6 months'],
      suggestedActions: ['Start document preparation immediately', 'Contact bank for guarantee', 'Engage ISO consultant', 'Post job for Fiber Engineer'],
      readinessScore: '82%',
      probabilityOfSuccess: '75%',
      recommendation: 'Proceed with bid preparation after addressing critical gaps in documentation and resources',
    },
    gapItems: [
      { category: 'Engineers', itemType: 'missing_engineer', itemName: 'Senior Fiber Engineer', description: 'Required for fiber optic installation', priority: 'high', department: 'Engineering' },
      { category: 'Certificates', itemType: 'missing_certificate', itemName: 'ISO 27001', description: 'Information security management', priority: 'medium', department: 'Quality' },
      { category: 'Equipment', itemType: 'missing_equipment', itemName: 'Cable Blowing Machine', description: 'Required for cable installation', priority: 'medium', department: 'Procurement' },
      { category: 'Documents', itemType: 'missing_document', itemName: 'Bank Guarantee', description: 'Required financial guarantee', priority: 'high', department: 'Finance' },
    ],
    tasks: [
      { department: 'Finance', title: 'Prepare Bank Guarantee', description: 'Obtain bank guarantee from financial institution', priority: 'high' },
      { department: 'HR', title: 'Assign Fiber Engineers', description: 'Identify and assign qualified fiber engineers', priority: 'high' },
      { department: 'Engineering', title: 'Review Technical Specifications', description: 'Detailed review of all technical requirements', priority: 'medium' },
      { department: 'Procurement', title: 'Arrange Required Equipment', description: 'Source and arrange all required equipment', priority: 'medium' },
      { department: 'Legal', title: 'Review Contract Conditions', description: 'Legal review of all contract terms', priority: 'high' },
      { department: 'Quality', title: 'Obtain ISO 27001 Certificate', description: 'Apply for ISO 27001 certification', priority: 'medium' },
      { department: 'BD', title: 'Prepare Technical Proposal', description: 'Develop comprehensive technical proposal', priority: 'high' },
    ],
  };
}

export default AiTenderAnalyzer;
